import { LoyaltyServiceOfferType } from 'generated/graphql-gateway';
import {
  AppOffer,
  AppOfferGroup,
  AppOfferListItem,
  AppPersonalizedOfferGroup,
} from 'state/loyalty/types';

import {
  ServiceOffer,
  ServiceOfferGroup,
  ServiceOfferItem,
  ServicePersonalizedOfferGroup,
} from '../types';

export function toBlockContent(value: unknown): { localeRaw: any[] } {
  if (!value) {
    return { localeRaw: [] };
  }
  if (typeof value === 'object' && !Array.isArray(value) && (value as any).localeRaw) {
    const raw = (value as any).localeRaw;
    return { localeRaw: Array.isArray(raw) ? raw : [] };
  }
  if (Array.isArray(value)) {
    return { localeRaw: value };
  }
  if (typeof value === 'string') {
    return { localeRaw: [{ _type: 'block', children: [{ _type: 'span', text: value }] }] };
  }
  return { localeRaw: [] };
}

export function mapServiceOffer(offer: ServiceOffer): AppOffer {
  const renderable = offer.renderable ?? {};

  return ({
    _id: offer.sanityId ?? offer.id,
    _type: offer.type === LoyaltyServiceOfferType.PERSONALIZED ? 'configOffer' : 'systemwideOffer',
    cmsId: offer.sanityId ?? offer.id,
    loyaltyEngineId: offer.loyaltyEngineId ?? offer.id,
    configId: offer.configId ?? undefined,
    type: (offer.type as unknown) as AppOffer['type'],
    redemptionType: (offer.redemptionType as unknown) as AppOffer['redemptionType'],
    endDate: offer.endDate ?? undefined,
    expirationDate: offer.expirationDate ?? undefined,
    isStackable: offer.isStackable ?? undefined,
    metadata: offer.metadata ?? undefined,
    errors: offer.errors ?? undefined,
    storeRestrictionMode: offer.storeRestrictionMode ?? undefined,
    availableStoreIds: offer.availableStoreIds ? [...offer.availableStoreIds] : undefined,
    offerServiceMode: offer.offerServiceMode ?? undefined,
    benefits: offer.benefits as any,
    templateId: offer.templateId ?? undefined,
    incentives: offer.incentives ?? undefined,
    vendorConfigs: offer.vendorConfigs ?? undefined,

    name: toBlockContent(renderable.name ?? offer.name),
    description: toBlockContent(renderable.description ?? offer.description),
    localizedImage: renderable.localizedImage ?? offer.localizedImage,
    moreInfo: toBlockContent(renderable.moreInfo ?? offer.moreInfo),
    howToRedeem: toBlockContent(renderable.howToRedeem ?? offer.howToRedeem),
    backgroundImage: renderable.backgroundImage ?? undefined,
    // The loyalty service has no daypart column; the offer's dayparts only
    // exist on the CMS document it renders from.
    daypart: renderable.daypart ?? undefined,

    inlineAlertMessage: (renderable.inlineAlertMessage as string) ?? undefined,

    __typename:
      offer.type === LoyaltyServiceOfferType.PERSONALIZED
        ? 'LoyaltyPersonalizedOffer'
        : 'LoyaltyOffer',
    __apptype:
      offer.type === LoyaltyServiceOfferType.PERSONALIZED
        ? ('AppPersonalizedOffer' as const)
        : ('AppSystemwideOffer' as const),
  } as unknown) as AppOffer;
}

function mapServiceOfferGroup(group: ServiceOfferGroup): AppOfferGroup {
  const renderable = group.renderable ?? {};
  const offers = group.offers.map(mapServiceOffer);

  return ({
    _id: group.cmsId ?? group.id,
    cmsId: group.cmsId ?? group.id,
    canOnlyChooseOne: group.canOnlyChooseOne,
    name: toBlockContent(renderable.name ?? group.groupName),
    description: toBlockContent(renderable.description ?? group.description),
    localizedImage: renderable.localizedImage ?? group.localizedImage,
    inlineAlertMessage: (renderable.inlineAlertMessage as string) ?? undefined,
    offers,
    __typename: 'LoyaltyOfferGroup',
    __apptype: 'AppOfferGroup' as const,
  } as unknown) as AppOfferGroup;
}

function mapServicePersonalizedOfferGroup(
  group: ServicePersonalizedOfferGroup
): AppPersonalizedOfferGroup {
  const renderable = group.renderable ?? {};
  const offers = group.offers.map(mapServiceOffer);

  return ({
    id: group.id,
    loyaltyId: group.loyaltyId ?? undefined,
    baseLoyaltyEngineId: group.baseLoyaltyEngineId ?? undefined,
    cmsId: group.cmsId ?? group.id,
    canOnlyChooseOne: group.canOnlyChooseOne,
    createdAt: group.createdAt ?? undefined,
    updatedAt: group.updatedAt ?? undefined,
    name: toBlockContent(renderable.name ?? group.groupName),
    description: toBlockContent(renderable.description ?? group.description),
    localizedImage: renderable.localizedImage ?? group.localizedImage,
    inlineAlertMessage: (renderable.inlineAlertMessage as string) ?? undefined,
    offers,
    __typename: 'LoyaltyPersonalizedOfferGroup',
    __apptype: 'AppPersonalizedOfferGroup' as const,
  } as unknown) as AppPersonalizedOfferGroup;
}

export function mapServiceOfferItem(item: ServiceOfferItem): AppOfferListItem {
  switch (item.__typename) {
    case 'LoyaltyServiceOfferGroup':
      return mapServiceOfferGroup(item);
    case 'LoyaltyServicePersonalizedOfferGroup':
      return mapServicePersonalizedOfferGroup(item);
    case 'LoyaltyServiceOffer':
    default:
      return mapServiceOffer(item);
  }
}
